import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { getFormatedNumber } from "app/tools/number"
import { IReportCreativeRow, ISummaryPageInfo } from "form/model"
import { MediaType } from "media/components/MediaType"
import { UnexpectedErrorPage } from "page/components/UnexpectedErrorPage"
import React from "react"
import { getCreativesReportRows } from "reports/tools/summary-reports"
import { ColumnHeader } from "ui/components/ColumnHeader"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { ListIdCell } from "ui/components/ListIdCell"
import { getDataGridTexts } from "ui/tools/list"

export const ReportCreatives = React.memo(({ form }: ISummaryPageInfo): JSX.Element => {
  const [rows, setRows] = React.useState<IReportCreativeRow[] | null>(null)
  const [error, setError] = React.useState("")
  React.useEffect(() => {
    if (rows !== null || error) {
      return
    }
    getCreativesReportRows(form.campaignId).then(result => {
      if (typeof result === "string") {
        setError(result)
        return
      }
      setRows(result)
    })
  })
  if (error) {
    return <UnexpectedErrorPage message={error} />
  }
  if (rows === null) {
    return <LinearLoader text={translate("form.report.loading")} />
  }
  const columns: Array<GridColDef<IReportCreativeRow>> = [
    {
      field: "type",
      headerName: translate("label.type"),
      flex: 3,
      renderCell: params => <MediaType type={params.row.type} />
    },
    {
      field: "name",
      headerName: translate("label.name"),
      flex: 3,
      renderCell: params => <ListIdCell label={params.row.name || "-"} id={params.row.id} />
    },
    {
      field: "dimension",
      headerName: translate("label.size"),
      flex: 2,
      renderCell: ({ row }) => (row.dimension ? `${row.dimension}px` : "")
    },
    {
      field: "impressions_without_multiplier",
      renderHeader: () => (
        <ColumnHeader
          primaryText={translate("label.impressions")}
          secondaryText={translate("label.impression.no.multiplier")}
        />
      ),
      renderCell: params => getFormatedNumber(Number(params.row.impressions_without_multiplier.toFixed(2))),
      flex: 2
    },
    {
      field: "impressions_with_multiplier",
      renderHeader: () => (
        <ColumnHeader
          primaryText={translate("label.impressions")}
          secondaryText={translate("label.impression.with.multiplier")}
        />
      ),
      renderCell: params => getFormatedNumber(Number(params.row.impressions_with_multiplier.toFixed(2))),
      flex: 2
    },
    {
      field: "screens",
      headerName: translate("label.screens"),
      flex: 2,
      renderCell: params => getFormatedNumber(params.row.screens)
    }
  ]
  return (
    <FlexBox
      vertical
      ai="start"
      jc="start"
      sx={{
        "width": "100%",
        "p": "1rem",
        "pb": "1.5rem",
        "mb": "3rem",
        "animation": "fadeIn 600ms",
        "flexGrow": "1",
        "overflow": "auto",
        "& > *": {
          width: "100%"
        },
        "& .MuiDataGrid-selectedRowCount": {
          visibility: "hidden"
        },
        "& .MuiDataGrid-cell": {
          fontSize: ".8rem",
          fontWeight: 300
        },
        "& .MuiDataGrid-row": {
          cursor: "default"
        },
        "& .MuiDataGrid-row.Mui-selected": {
          backgroundColor: "transparent"
        },
        "& .targeted-check": {
          "color": theme => theme.palette.text.primary2,
          "& svg": {
            ml: "1rem"
          }
        },
        "& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-selected:hover": {
          backgroundColor: "transparent !important"
        },
        "& .icon": {
          color: theme => theme.palette.text.primary2
        },
        "& .icon.non-targeted": {
          color: theme => theme.palette.text.secondary
        },
        "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd)": {
          backgroundColor: theme => theme.palette.background.default
        },
        "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd):hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)"
        },
        "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
          {
            outline: "none !important"
          }
      }}>
      <DataGrid
        rows={rows}
        getRowId={row => `${row.id}-${row.type}-${row.name}`}
        columns={columns}
        autoHeight
        disableColumnFilter
        disableColumnMenu
        className="report-creatives-list"
        rowHeight={50}
        localeText={getDataGridTexts()}
        sx={{ mt: "1rem", height: "22rem", backgroundColor: theme => theme.palette.background.paper }}
      />
    </FlexBox>
  )
})
